import { logger } from '@/utils/logger'
import type { CsvRow, DataPoint } from '@/types/api'

export interface Candle extends DataPoint {}

// --- Date format types (Step 2) ---

export type DateFormat = 'european' | 'us'

export class AmbiguousDateFormatError extends Error {
  name = 'AmbiguousDateFormatError' as const
  sampleDates: string[]
  constructor(sampleDates: string[]) {
    super('Unable to determine date format automatically')
    this.sampleDates = sampleDates
  }
}

// --- Constants ---

const TS_MILLISECONDS_THRESHOLD = 1_000_000_000_000

const KEY_ALIASES: Record<string, string> = {
  timestamp: 'timestamp',
  time: 'timestamp',
  'and time': 'timestamp',
  date: 'timestamp',
  ts_event: 'timestamp'
}

// Matches DD-MM-YYYY or MM-DD-YYYY with optional time component
const LOCALIZED_DATE_RE = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})(?:[\sT](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/

/**
 * Detects if a row is in Databento format (nested hd.ts_event structure)
 * Databento format example:
 * {
 *   "hd": { "ts_event": "2025-12-14T23:00:00.000000000Z", ... },
 *   "open": "6831.000000000",
 *   "high": "6838.000000000",
 *   ...
 * }
 */
function isDatabentoFormat(row: Record<string, unknown>): boolean {
  if (!row || typeof row !== 'object') return false
  const hd = row.hd
  if (!hd || typeof hd !== 'object') return false
  return 'ts_event' in (hd as Record<string, unknown>)
}

/**
 * Transforms a Databento format row to a flat structure
 * Extracts ts_event from hd object and places it at root level as timestamp
 */
function flattenDatabentoRow(row: Record<string, unknown>): Record<string, unknown> {
  const hd = row.hd as Record<string, unknown>
  const flattened: Record<string, unknown> = { ...row }

  // Extract timestamp from hd.ts_event
  if (hd && typeof hd === 'object' && 'ts_event' in hd) {
    flattened.timestamp = hd.ts_event
  }

  // Remove the hd object from the flattened result as it's metadata
  delete flattened.hd

  return flattened
}

function normaliseKey(key: string): string {
  const trimmed = key.trim().replace(/^['"]|['"]$/g, '')
  const lowered = trimmed.toLowerCase()
  if (!lowered) return ''

  if (KEY_ALIASES[lowered]) {
    return KEY_ALIASES[lowered]
  }

  const collapsed = lowered.replace(/_/g, '')
  if (KEY_ALIASES[collapsed]) {
    return KEY_ALIASES[collapsed]
  }

  if (lowered.endsWith(' time')) {
    return 'timestamp'
  }

  return lowered
}

/**
 * Parses a localized date string (DD-MM-YYYY or MM-DD-YYYY, with optional time)
 * using the specified format. Returns ms since epoch (local time) or null if not matched.
 */
function parseLocalizedDate(value: string, dateFormat?: DateFormat): number | null {
  const match = value.match(LOCALIZED_DATE_RE)
  if (!match || !match[1] || !match[2] || !match[3]) return null

  const firstNum = parseInt(match[1])
  const secondNum = parseInt(match[2])
  const year = parseInt(match[3])
  const hours = match[4] ? parseInt(match[4]) : undefined
  const minutes = match[5] ? parseInt(match[5]) : undefined
  const seconds = match[6] ? parseInt(match[6]) : undefined

  // Validate time component ranges
  if ((hours ?? 0) > 23 || (minutes ?? 0) > 59 || (seconds ?? 0) > 59) {
    throw new Error(`Invalid time: ${value}`)
  }

  const h = hours ?? 0
  const m = minutes ?? 0
  const s = seconds ?? 0

  let day: number
  let month: number

  if (dateFormat === 'european') {
    day = firstNum
    month = secondNum
  } else if (dateFormat === 'us') {
    month = firstNum
    day = secondNum
  } else {
    // No format hint — per-row heuristic
    if (firstNum > 12) {
      day = firstNum
      month = secondNum
    } else if (secondNum > 12) {
      month = firstNum
      day = secondNum
    } else {
      // Ambiguous — default European
      day = firstNum
      month = secondNum
    }
  }

  // Construct in local time (matches Date.parse() convention for non-ISO formats)
  const d = new Date(year, month - 1, day, h, m, s)

  // Validate date components (catches impossible dates like Feb 31)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
    throw new Error(`Invalid date: ${value}`)
  }

  return d.getTime()
}

function parseIsoLikeTimestamp(value: string, dateFormat?: DateFormat): number {
  let candidate = value.trim()
  if (!candidate) {
    throw new Error('timestamp string is empty')
  }

  // Try localized date format first (DD-MM-YYYY, MM-DD-YYYY with optional time)
  const localized = parseLocalizedDate(candidate, dateFormat)
  if (localized !== null) {
    return localized
  }

  if (candidate.endsWith('Z')) {
    candidate = `${candidate.substring(0, candidate.length - 1)}+00:00`
  }

  const candidates = new Set<string>([candidate])
  if (!candidate.includes('T') && candidate.includes(' ')) {
    candidates.add(candidate.replace(' ', 'T'))
  }
  if (!candidate.includes('T') && !candidate.includes(' ') && candidate.length === 10) {
    candidates.add(`${candidate}T00:00:00`)
  }

  for (const attempt of candidates) {
    const parsed = Date.parse(attempt)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
  }

  throw new Error(`Unsupported timestamp format: ${value}`)
}

function normaliseTimestamp(raw: unknown, dateFormat?: DateFormat): number {
  if (raw === null || raw === undefined) {
    throw new Error('timestamp is missing')
  }

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      throw new Error('timestamp is not finite')
    }
    const numeric = raw < TS_MILLISECONDS_THRESHOLD ? raw * 1000 : raw
    return Math.round(numeric)
  }

  const asString = String(raw).trim()
  if (!asString) {
    throw new Error('timestamp is empty')
  }

  if (!Number.isNaN(Number(asString)) && /^[-+]?\d+(\.\d+)?$/.test(asString)) {
    const numeric = Number(asString)
    if (!Number.isFinite(numeric)) {
      throw new Error('timestamp is not finite')
    }
    const adjusted = numeric < TS_MILLISECONDS_THRESHOLD ? numeric * 1000 : numeric
    return Math.round(adjusted)
  }

  const parsed = parseIsoLikeTimestamp(asString, dateFormat)
  return Math.round(parsed)
}

function parseFloatStrict(raw: unknown, field: string): number {
  if (raw === null || raw === undefined) {
    throw new Error(`${field} is missing`)
  }

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      throw new Error(`${field} is not finite`)
    }
    return raw
  }

  const asString = String(raw).trim()
  if (!asString) {
    throw new Error(`${field} is empty`)
  }

  const numeric = Number(asString)
  if (!Number.isFinite(numeric)) {
    throw new Error(`${field} value '${asString}' is not numeric`)
  }

  return numeric
}

function parseVolume(raw: unknown, fallback: number): number {
  if (raw === null || raw === undefined || raw === '') {
    return fallback
  }

  try {
    return parseFloatStrict(raw, 'volume')
  } catch (error) {
    logger.debug?.('Volume parse failed, using fallback', {
      error: error instanceof Error ? error.message : String(error)
    })
    return fallback
  }
}

function normaliseRow(row: CsvRow | Record<string, unknown>): Record<string, unknown> {
  const normalised: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(row)) {
    if (!key) continue
    const mappedKey = normaliseKey(key)
    if (!mappedKey) continue
    if (typeof value === 'string') {
      normalised[mappedKey] = value.trim()
    } else {
      normalised[mappedKey] = value
    }
  }
  return normalised
}

// --- Date format detection (Step 2) ---

const NUMERIC_RE = /^\d+$/
const ISO_PREFIX_RE = /^\d{4}[-\/]/
const DMY_OR_MDY_RE = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})\b/

/**
 * Scans all timestamp strings to determine the date format for the entire file.
 * Returns 'european' or 'us'. Throws AmbiguousDateFormatError if indeterminate.
 */
export function detectDateFormat(timestamps: string[]): DateFormat {
  let seenEuropean = false
  let seenUS = false
  const ambiguousSamples: string[] = []

  for (const ts of timestamps) {
    const trimmed = ts.trim()

    // Skip numeric (Unix) timestamps
    if (NUMERIC_RE.test(trimmed)) continue

    // Skip ISO timestamps (YYYY-MM-DD...)
    if (ISO_PREFIX_RE.test(trimmed)) continue

    // Check for DD-MM-YYYY / MM-DD-YYYY patterns
    const match = trimmed.match(DMY_OR_MDY_RE)
    if (!match || !match[1] || !match[2]) continue

    const firstNum = parseInt(match[1])
    const secondNum = parseInt(match[2])

    if (firstNum > 12 && secondNum >= 1 && secondNum <= 12) {
      seenEuropean = true
    } else if (secondNum > 12 && firstNum >= 1 && firstNum <= 12) {
      seenUS = true
    } else if (firstNum >= 1 && firstNum <= 12 && secondNum >= 1 && secondNum <= 12) {
      // Both ≤ 12 — ambiguous for this row
      if (ambiguousSamples.length < 3) {
        ambiguousSamples.push(trimmed)
      }
    }

    // Contradictory formats found
    if (seenEuropean && seenUS) {
      throw new Error(
        'Contradictory date formats detected: some rows appear DD-MM-YYYY while others appear MM-DD-YYYY'
      )
    }
  }

  if (seenEuropean) return 'european'
  if (seenUS) return 'us'

  // All examined dates were ambiguous (both values ≤ 12)
  if (ambiguousSamples.length > 0) {
    throw new AmbiguousDateFormatError(ambiguousSamples)
  }

  // No DD-MM / MM-DD patterns found (all ISO/numeric) — safe default
  return 'european'
}

// --- Main conversion (Step 4) ---

export function convertRowsToCandles(
  rows: Array<CsvRow | Record<string, unknown>>,
  defaultVolume = 0,
  dateFormatOverride?: DateFormat,
  warnings?: string[]
): Candle[] {
  const candles: Candle[] = []

  // Detect if this is Databento format by checking the first row
  const firstRow = rows[0]
  const isDatabentoData = firstRow && isDatabentoFormat(firstRow as Record<string, unknown>)
  if (isDatabentoData) {
    logger.info?.('Detected Databento format - applying transformation')
  }

  // --- Pass 1: Collect timestamp strings for format detection ---
  let dateFormat: DateFormat | undefined = dateFormatOverride
  if (!dateFormat) {
    const timestampStrings: string[] = []
    for (const row of rows) {
      const processedRow = isDatabentoData
        ? flattenDatabentoRow(row as Record<string, unknown>)
        : row
      const normalisedRow = normaliseRow(processedRow)
      const tsRaw = normalisedRow.timestamp ?? normalisedRow.time
      if (tsRaw !== undefined && typeof tsRaw === 'string' && !NUMERIC_RE.test(tsRaw.trim())) {
        timestampStrings.push(tsRaw)
      }
    }

    if (timestampStrings.length > 0) {
      dateFormat = detectDateFormat(timestampStrings)
    }
  }

  // --- Pass 2: Convert rows to candles ---
  let droppedCount = 0
  let firstDropError = ''

  rows.forEach((row, index) => {
    // Transform Databento format rows to flat structure if needed
    const processedRow = isDatabentoData
      ? flattenDatabentoRow(row as Record<string, unknown>)
      : row
    const normalisedRow = normaliseRow(processedRow)
    const timestampRaw = normalisedRow.timestamp ?? normalisedRow.time
    if (timestampRaw === undefined) {
      droppedCount++
      if (!firstDropError) firstDropError = `row ${index}: missing timestamp`
      logger.debug?.('Skipping row without timestamp', { index })
      return
    }

    try {
      const timestamp = normaliseTimestamp(timestampRaw, dateFormat)
      const open = parseFloatStrict(normalisedRow.open, 'open')
      const high = parseFloatStrict(normalisedRow.high, 'high')
      const low = parseFloatStrict(normalisedRow.low, 'low')
      const close = parseFloatStrict(normalisedRow.close, 'close')
      const volume = parseVolume(normalisedRow.volume, defaultVolume)

      candles.push({ timestamp, open, high, low, close, volume })
    } catch (error) {
      droppedCount++
      const msg = error instanceof Error ? error.message : String(error)
      if (!firstDropError) firstDropError = `row ${index}: ${msg}`
      logger.warn?.('Skipping invalid row', { index, error: msg })
    }
  })

  // Post-loop: report dropped rows
  if (droppedCount > 0 && warnings) {
    warnings.push(
      `${droppedCount} of ${rows.length} rows were skipped due to parse errors (first error: ${firstDropError})`
    )
  }

  if (candles.length === 0) {
    throw new Error('No valid candle rows produced from supplied data')
  }

  candles.sort((a, b) => a.timestamp - b.timestamp)
  return candles
}

export function createNormalisedJson(candles: Candle[]): string {
  return JSON.stringify(candles, null, 2)
}
